import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  IconButton,
} from '@mui/material';
import { 
  Send as SendIcon, 
  SmartToy as AgentIcon, 
  Person as UserIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { agentApi, chatApi } from '../services/api';
import { Agent, ChatMessage, EndCondition, EndConditionResult } from '../types';
import { useLoading } from '../context/LoadingContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useNotification } from '../context/NotificationContext';
import EndConditionStatus from '../components/EndConditionStatus';

const Chat = () => {
  const { agentId: urlAgentId } = useParams<{ agentId: string }>();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(urlAgentId);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { startLoading, stopLoading } = useLoading();
  const { handleError } = useErrorHandler();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // End conditions state
  const [endConditions, setEndConditions] = useState<EndCondition[]>([]);
  const [conditionResults, setConditionResults] = useState<EndConditionResult[]>([]);
  const [isLoadingConditions, setIsLoadingConditions] = useState(false);

  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [hasAgentsError, setHasAgentsError] = useState(false);

  const fetchAgents = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isLoadingAgents) return;
    
    try {
      setIsLoadingAgents(true);
      startLoading();
      const data = await agentApi.getAgents();
      setAgents(data);
      setHasAgentsError(false);
      
      // If no agent is selected and we have agents, select the first one
      if (!selectedAgentId && data.length > 0) {
        setSelectedAgentId(data[0].id);
        navigate(`/chat/${data[0].id}`, { replace: true });
      }
    } catch (error) {
      handleError(error);
      setHasAgentsError(true);
      console.error('Failed to fetch agents:', error);
    } finally {
      setIsLoadingAgents(false);
      stopLoading();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError, selectedAgentId, navigate]);

  useEffect(() => {
    // Only fetch if we haven't had an error yet
    if (!hasAgentsError) {
      fetchAgents();
    }
  }, [fetchAgents, hasAgentsError]);
  
  // Add a retry button for when we have errors
  const handleRetryAgents = () => {
    setHasAgentsError(false); // Reset error state
    fetchAgents();
  };

  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [hasChatError, setHasChatError] = useState(false);

  // Fetch agent details to get end conditions
  const fetchAgentDetails = useCallback(async (agentId: string) => {
    try {
      console.log('Fetching agent details for:', agentId);
      const agentDetails = await agentApi.getAgentDetails(agentId);
      console.log('Agent details received:', agentDetails);
      console.log('End conditions:', agentDetails.endConditions);
      
      // Make sure we have a valid array of end conditions
      if (Array.isArray(agentDetails.endConditions)) {
        // Ensure each end condition has a parameters object
        const validatedEndConditions = agentDetails.endConditions.map(condition => ({
          ...condition,
          parameters: condition.parameters || {}
        }));
        setEndConditions(validatedEndConditions);
      } else {
        console.log('No end conditions found or invalid format');
        setEndConditions([]);
      }
    } catch (error) {
      handleError(error);
      console.error('Failed to fetch agent details:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]);
  
  // Fetch end conditions status
  const fetchEndConditions = useCallback(async (agentId: string) => {
    if (isLoadingConditions) return;
    
    try {
      console.log('Checking end conditions for:', agentId);
      setIsLoadingConditions(true);
      const response = await chatApi.checkEndConditions(agentId);
      console.log('End condition results:', response.results);
      setConditionResults(response.results);
      
      // If all conditions are met, show a notification
      if (response.allConditionsMet && response.results.length > 0) {
        showNotification('All end conditions have been met!', 'success');
      }
    } catch (error) {
      handleError(error);
      console.error('Failed to check end conditions:', error);
    } finally {
      setIsLoadingConditions(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]);

  const fetchChatHistory = useCallback(async (agentId: string) => {
    // Prevent multiple simultaneous requests
    if (isLoadingChat) return;
    
    try {
      setIsLoadingChat(true);
      startLoading();
      const data = await chatApi.getChatHistory(agentId);
      setChatHistory(data.messages);
      setHasChatError(false);
    } catch (error) {
      handleError(error);
      setHasChatError(true);
      console.error('Failed to fetch chat history:', error);
    } finally {
      setIsLoadingChat(false);
      stopLoading();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]);

  useEffect(() => {
    if (selectedAgentId) {
      console.log('Selected agent changed to:', selectedAgentId);
      
      // Only fetch if we haven't had an error yet
      if (!hasChatError) {
        fetchChatHistory(selectedAgentId);
      }
      
      // Fetch agent details to get end conditions
      fetchAgentDetails(selectedAgentId);
      
      // Check end conditions status
      fetchEndConditions(selectedAgentId);
    } else {
      setChatHistory([]);
      setEndConditions([]);
      setConditionResults([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentId]);
  
  // Add a retry button for when we have errors
  const handleRetryChat = () => {
    if (selectedAgentId) {
      setHasChatError(false); // Reset error state
      fetchChatHistory(selectedAgentId);
    }
  };

  useEffect(() => {
    // Scroll to bottom when chat history changes
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const handleAgentChange = (event: SelectChangeEvent<string>) => {
    const newAgentId = event.target.value;
    setSelectedAgentId(newAgentId);
    navigate(`/chat/${newAgentId}`);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };


  
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAgentId || !message.trim() || isSending) {
      return;
    }
    
    try {
      setIsSending(true);
      
      // Optimistically add user message to chat
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      };
      
      setChatHistory((prev) => [...prev, userMessage]);
      setMessage('');
      
      // Send message to API
      const response = await chatApi.sendMessage(selectedAgentId, message);
      
      // Add agent response to chat
      const agentMessage: ChatMessage = {
        id: `response-${Date.now()}`,
        role: 'assistant',
        content: typeof response.message === 'string' ? response.message : response.message.content,
        timestamp: response.message.timestamp || response.timestamp || new Date().toISOString(),
      };
      
      setChatHistory((prev) => [...prev, agentMessage]);
      
      // Check end conditions after sending a message
      if (selectedAgentId) {
        fetchEndConditions(selectedAgentId);
      }
    } catch (error) {
      handleError(error);
      // Don't remove the optimistic message on error as the ID won't match
      // Instead, show an error message in the chat
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, there was an error processing your message. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setChatHistory((prev) => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        {/* Top bar with title, agent selector, and end conditions */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" component="h1">
              Chat
            </Typography>
            
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="agent-select-label">Select Agent</InputLabel>
              <Select
                labelId="agent-select-label"
                id="agent-select"
                value={selectedAgentId || ''}
                onChange={handleAgentChange}
                label="Select Agent"
              >
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {/* End Condition Status in top bar */}
          {selectedAgentId && endConditions && endConditions.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 2, minWidth: '150px' }}>
                  End Conditions Status:
                </Typography>
                <Box sx={{ flexGrow: 1, maxWidth: 'calc(100% - 220px)' }}>
                  <EndConditionStatus
                    endConditions={endConditions}
                    conditionResults={conditionResults}
                    isLoading={isLoadingConditions}
                    onRefresh={() => selectedAgentId && fetchEndConditions(selectedAgentId)}
                  />
                </Box>
              </Box>
              <Box sx={{ ml: 2 }}>
                <Tooltip title="Refresh end condition status">
                  <IconButton 
                    onClick={() => selectedAgentId && fetchEndConditions(selectedAgentId)}
                    disabled={isLoadingConditions}
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              height: 'calc(100vh - 240px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                overflow: 'auto',
                p: 2,
              }}
            >
              {hasAgentsError ? (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="body1" color="error" gutterBottom>
                    Failed to load agents. There might be a connection issue with the backend server.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRetryAgents}
                    sx={{ mt: 2 }}
                  >
                    Retry Loading Agents
                  </Button>
                </Box>
              ) : hasChatError && selectedAgentId ? (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="body1" color="error" gutterBottom>
                    Failed to load chat history. There might be a connection issue with the backend server.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRetryChat}
                    sx={{ mt: 2 }}
                  >
                    Retry Loading Chat
                  </Button>
                </Box>
              ) : chatHistory.length === 0 ? (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {selectedAgentId
                      ? 'No messages yet. Start a conversation!'
                      : 'Select an agent to start chatting'}
                  </Typography>
                </Box>
              ) : (
                <List>
                  {chatHistory.map((msg, index) => (
                    <React.Fragment key={msg.id}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{
                          flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                            ml: msg.role === 'user' ? 2 : 0,
                            mr: msg.role === 'user' ? 0 : 2,
                          }}
                        >
                          {msg.role === 'user' ? <UserIcon /> : <AgentIcon />}
                        </Avatar>
                        <ListItemText
                          primary={msg.role === 'user' ? 'You' : agents.find(a => a.id === selectedAgentId)?.name || 'Agent'}
                          secondary={
                            <Box>
                              <Typography
                                component="span"
                                variant="body1"
                                sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
                              >
                                {msg.content}
                              </Typography>
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 1, textAlign: msg.role === 'user' ? 'right' : 'left' }}
                              >
                                {new Date(msg.timestamp || (msg.createdAt || '')).toLocaleTimeString()}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            bgcolor: msg.role === 'user' ? 'primary.light' : 'background.paper',
                            p: 2,
                            borderRadius: 2,
                            maxWidth: '80%',
                          }}
                        />
                      </ListItem>
                      {index < chatHistory.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                  <div ref={messagesEndRef} />
                </List>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              component="form"
              onSubmit={handleSendMessage}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                placeholder="Type your message..."
                value={message}
                onChange={handleMessageChange}
                disabled={!selectedAgentId || isSending}
                variant="outlined"
                size="medium"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!selectedAgentId || !message.trim() || isSending}
                endIcon={<SendIcon />}
              >
                Send
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Chat;
